import { normalizePostcodeInput } from "../addressUtils";
import { nullToEmptyString } from "../form/formValueNormalization";

export interface GooglePlaceAddressFields {
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    town: string;
    county: string;
    postcode: string;
    country: string;
}

/** Geocoder components or Places API (New) `AddressComponent`. */
export type GoogleAddressComponent =
    | google.maps.GeocoderAddressComponent
    | google.maps.places.AddressComponent;

function isPlacesAddressComponent(
    component: GoogleAddressComponent
): component is google.maps.places.AddressComponent {
    return "longText" in component;
}

export function componentLongName(component: GoogleAddressComponent): string {
    if (isPlacesAddressComponent(component)) {
        return component.longText ?? "";
    }
    return component.long_name ?? "";
}

function componentShortName(component: GoogleAddressComponent): string {
    if (isPlacesAddressComponent(component)) {
        return component.shortText ?? "";
    }
    return component.short_name ?? "";
}

export function findComponent(
    components: GoogleAddressComponent[],
    type: string,
    useShortName = false
): string {
    const match = components.find((c) => c.types.includes(type));
    if (!match) return "";
    return useShortName ? componentShortName(match) : componentLongName(match);
}

export function normalizeAddressText(value: string): string {
    return value.trim().toLowerCase().replace(/\s+/g, " ");
}

/** Exact normalised equality - used for cross-field deduplication. */
export function isExactAddressMatch(a: string, b: string): boolean {
    const left = normalizeAddressText(a);
    const right = normalizeAddressText(b);
    if (!left || !right) return false;
    return left === right;
}

/** True when two address strings refer to the same line (includes substring match). */
export function isEffectivelySameAddressLine(a: string, b: string): boolean {
    const left = normalizeAddressText(a);
    const right = normalizeAddressText(b);
    if (!left || !right) return false;
    return left === right || left.includes(right) || right.includes(left);
}

const STREET_ABBREVIATION_REPLACEMENTS: ReadonlyArray<readonly [RegExp, string]> = [
    [/\brd\b/g, "road"],
    [/\bave\b/g, "avenue"],
    [/\bav\b/g, "avenue"],
    [/\bst\b/g, "street"],
    [/\bln\b/g, "lane"],
    [/\bdr\b/g, "drive"],
    [/\bct\b/g, "court"],
    [/\bpl\b/g, "place"],
];

/** Normalise street text for duplicate comparison (abbreviations, punctuation). */
export function normalizeStreetEquivalent(value: string): string {
    let norm = normalizeAddressText(value);
    for (const [pattern, replacement] of STREET_ABBREVIATION_REPLACEMENTS) {
        norm = norm.replace(pattern, replacement);
    }
    return norm.replace(/[.,]/g, "").trim();
}

/** True when the value looks like a Google Plus Code (optionally with locality suffix). */
export function isPlusCodeAddressLine(value: string): boolean {
    const trimmed = value.trim();
    if (!trimmed) return false;
    const codePart = trimmed.split(",")[0]?.trim() ?? trimmed;
    return /^[A-Z0-9]{4,8}\+[A-Z0-9]{2,3}/i.test(codePart);
}

/**
 * True when two address lines are duplicates for layout purposes.
 * Handles minor formatting differences (Rd vs Road) without collapsing distinct POI vs street lines.
 */
export function isDuplicateAddressSegment(a: string, b: string): boolean {
    if (!a.trim() || !b.trim()) return false;
    if (isExactAddressMatch(a, b)) return true;

    const left = normalizeStreetEquivalent(a);
    const right = normalizeStreetEquivalent(b);
    if (left === right) return true;

    const numLeft = left.match(/^\d+/)?.[0];
    const numRight = right.match(/^\d+/)?.[0];
    if (numLeft && numRight && numLeft === numRight) {
        if (left.includes(right) || right.includes(left)) {
            return true;
        }
    }

    if (isEffectivelySameAddressLine(a, b)) {
        const shorter = Math.min(left.length, right.length);
        const longer = Math.max(left.length, right.length);
        if (shorter >= 10 && shorter / longer >= 0.6) {
            return true;
        }
    }

    return false;
}

export interface AddressLineLayout {
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
}

/**
 * Final pass: non-empty line 1, no line-3-without-line-2, duplicate removal by priority.
 */
export function normalizeAddressLineLayout(
    lines: AddressLineLayout,
    ctx?: { town?: string; county?: string; postcode?: string }
): AddressLineLayout {
    let { addressLine1, addressLine2, addressLine3 } = lines;

    if (!addressLine1.trim() && addressLine2.trim()) {
        addressLine1 = addressLine2;
        addressLine2 = addressLine3;
        addressLine3 = "";
    }

    if (!addressLine1.trim() && addressLine3.trim()) {
        addressLine1 = addressLine3;
        addressLine2 = "";
        addressLine3 = "";
    }

    if (!addressLine2.trim() && addressLine3.trim()) {
        addressLine2 = addressLine3;
        addressLine3 = "";
    }

    const reserved = [ctx?.town, ctx?.county, ctx?.postcode].filter((p) => p?.trim()) as string[];

    const clearIfDuplicateOrReserved = () => {
        if (addressLine2 && isDuplicateAddressSegment(addressLine2, addressLine1)) {
            addressLine2 = "";
        }
        if (
            addressLine3 &&
            (isDuplicateAddressSegment(addressLine3, addressLine1) ||
                isDuplicateAddressSegment(addressLine3, addressLine2))
        ) {
            addressLine3 = "";
        }
        if (addressLine2 && addressLine3 && isDuplicateAddressSegment(addressLine2, addressLine3)) {
            addressLine3 = "";
        }
        for (const part of reserved) {
            if (addressLine2 && isExactAddressMatch(addressLine2, part)) {
                addressLine2 = "";
            }
            if (addressLine3 && isExactAddressMatch(addressLine3, part)) {
                addressLine3 = "";
            }
        }
        if (!addressLine2.trim() && addressLine3.trim()) {
            addressLine2 = addressLine3;
            addressLine3 = "";
        }
    };

    clearIfDuplicateOrReserved();

    return { addressLine1, addressLine2, addressLine3 };
}

export function streetLineIncludesRoute(streetLine: string, routeName: string): boolean {
    if (!streetLine.trim() || !routeName.trim()) return false;
    if (isEffectivelySameAddressLine(streetLine, routeName)) return true;

    const streetNorm = normalizeAddressText(streetLine);
    const routeNorm = normalizeAddressText(routeName);
    return streetNorm.endsWith(routeNorm);
}

export function findRouteComponent(components: GoogleAddressComponent[]): string {
    const route = findComponent(components, "route").trim();
    if (route) return route;

    const streetAddress = findComponent(components, "street_address").trim();
    if (!streetAddress) return "";

    return streetAddress.split(",")[0]?.trim() ?? "";
}

export interface ParsedGoogleAddressComponents {
    streetNumber: string;
    route: string;
    premise: string;
    subpremise: string;
    sublocality: string;
    locality: string;
    postalTown: string;
    county: string;
    postcode: string;
    country: string;
    fullStreetLine: string;
    routeAreaLine: string;
}

export function parseGoogleAddressComponents(components: GoogleAddressComponent[]): ParsedGoogleAddressComponents {
    const streetNumber = findComponent(components, "street_number").trim();
    const route = findComponent(components, "route").trim();
    const premise = findComponent(components, "premise").trim();
    const subpremise = findComponent(components, "subpremise").trim();
    const sublocality =
        findComponent(components, "sublocality") ||
        findComponent(components, "sublocality_level_1") ||
        findComponent(components, "neighborhood");
    const locality = findComponent(components, "locality").trim();
    const postalTown = findComponent(components, "postal_town").trim();
    const county =
        findComponent(components, "administrative_area_level_2") ||
        findComponent(components, "administrative_area_level_1");
    const postcode = findComponent(components, "postal_code").trim();
    const country = findComponent(components, "country").trim();

    let fullStreetLine = "";
    let routeAreaLine = "";

    if (streetNumber) {
        fullStreetLine = [streetNumber, route].filter((p) => p.trim().length > 0).join(" ").trim();
    } else if (premise) {
        fullStreetLine = premise;
        routeAreaLine =
            route && !isEffectivelySameAddressLine(route, premise) ? route : "";
    } else {
        fullStreetLine = route;
    }

    return {
        streetNumber,
        route,
        premise,
        subpremise,
        sublocality: sublocality.trim(),
        locality,
        postalTown,
        county: county.trim(),
        postcode,
        country,
        fullStreetLine,
        routeAreaLine,
    };
}

export function resolveTown(parsed: ParsedGoogleAddressComponents): string {
    return parsed.postalTown || parsed.locality || parsed.sublocality || "";
}

export function resolveSublocalityLine(parsed: ParsedGoogleAddressComponents, town: string): string {
    if (!parsed.sublocality.trim()) return "";
    if (isExactAddressMatch(parsed.sublocality, town)) return "";
    return parsed.sublocality.trim();
}

/** Village/locality between a named venue and the post town. */
export function resolveIntermediateLocalityFromComponents(
    components: GoogleAddressComponent[],
    town: string,
    displayName: string
): string {
    const candidates = [
        findComponent(components, "locality"),
        findComponent(components, "administrative_area_level_3"),
        findComponent(components, "administrative_area_level_4"),
    ];

    for (const candidate of candidates) {
        const value = candidate.trim();
        if (!value) continue;
        if (isExactAddressMatch(value, town)) continue;
        if (isEffectivelySameAddressLine(value, displayName)) continue;
        return value;
    }

    return "";
}

export function isFormattedAddressPartExcluded(
    part: string,
    ctx: { displayName: string; town: string; postcode: string; country: string }
): boolean {
    const norm = normalizeAddressText(part);
    if (!norm) return true;

    const normDisplay = normalizeAddressText(ctx.displayName);
    if (
        normDisplay &&
        (norm === normDisplay || norm.includes(normDisplay) || normDisplay.includes(norm))
    ) {
        return true;
    }

    const normTown = normalizeAddressText(ctx.town);
    const normCountry = normalizeAddressText(ctx.country);
    if (normCountry && (norm === normCountry || norm === "uk")) return true;

    const postcode = ctx.postcode.trim();
    if (postcode && part.toUpperCase().includes(postcode.toUpperCase())) {
        const withoutPostcode = part
            .replace(new RegExp(postcode.replace(/\s+/g, "\\s*"), "i"), "")
            .trim();
        const withoutPostcodeNorm = normalizeAddressText(withoutPostcode);
        if (!withoutPostcodeNorm) return true;
        if (normTown && withoutPostcodeNorm === normTown) return true;
    }

    if (normTown && norm === normTown) return true;

    return false;
}

export function extractDetailLinesFromFormattedAddress(
    formattedAddress: string,
    ctx: { displayName: string; town: string; postcode: string; country: string }
): string[] {
    if (!formattedAddress.trim()) return [];

    return formattedAddress
        .split(",")
        .map((part) => part.trim())
        .filter((part) => part.length > 0 && !isFormattedAddressPartExcluded(part, ctx));
}

export function buildResolvedAddressFields(
    lines: Pick<GooglePlaceAddressFields, "addressLine1" | "addressLine2" | "addressLine3">,
    parsed: ParsedGoogleAddressComponents,
    town: string
): GooglePlaceAddressFields {
    return {
        addressLine1: nullToEmptyString(lines.addressLine1),
        addressLine2: nullToEmptyString(lines.addressLine2),
        addressLine3: nullToEmptyString(lines.addressLine3),
        town: nullToEmptyString(town),
        county: nullToEmptyString(parsed.county),
        postcode: normalizePostcodeInput(nullToEmptyString(parsed.postcode)),
        country: nullToEmptyString(parsed.country) || "United Kingdom",
    };
}

/** Reads display name from Places API (New) `displayName` field variants. */
export function extractPlaceDisplayName(
    displayName: string | google.maps.places.Place["displayName"] | null | undefined
): string {
    if (displayName == null) return "";
    if (typeof displayName === "string") return displayName.trim();
    if (typeof displayName === "object" && "text" in displayName) {
        return String((displayName as { text?: string }).text ?? "").trim();
    }
    return String(displayName).trim();
}

/** Reads lat/lng from Places `location` (LatLng or literal). */
export function extractPlaceLatLng(
    location: google.maps.LatLng | google.maps.LatLngLiteral | null | undefined
): { lat: number; lng: number } | null {
    if (location == null) return null;

    const lat =
        typeof (location as google.maps.LatLng).lat === "function"
            ? (location as google.maps.LatLng).lat()
            : (location as google.maps.LatLngLiteral).lat;
    const lng =
        typeof (location as google.maps.LatLng).lng === "function"
            ? (location as google.maps.LatLng).lng()
            : (location as google.maps.LatLngLiteral).lng;

    if (typeof lat !== "number" || typeof lng !== "number" || Number.isNaN(lat) || Number.isNaN(lng)) {
        return null;
    }

    return { lat, lng };
}

/** Whether Location Name may be auto-filled from a place display name. */
export function shouldAutoFillLocationName(params: {
    currentName: string;
    nameEditedByUser: boolean;
    nameWasAutoFilled?: boolean;
    hasPersistedRecord: boolean;
}): boolean {
    if (params.nameEditedByUser) return false;
    if (params.hasPersistedRecord && params.currentName.trim().length > 0 && !params.nameWasAutoFilled) {
        return false;
    }
    return true;
}
